'use client';

import type { MockPersona } from '@/lib/mock';
import type { WidgetSettings } from '@/lib/settings';
import { ProfileContext } from './ProfileContext';
import { CampusSvgProvider } from './CampusIcon';
import { ToastProvider } from './ui';
import { HeroSkeleton, SignedOutCard } from './Hero';
import { PeopleSection } from './PeopleSection';
import { AccountSection } from './AccountSection';
import { CARD, CardsSkeleton, Section } from './ui';
import { useProfileSession } from './useProfileSession';

export interface ProfileWidgetProps {
  apiBaseUrl: string;
  /** In-memory family instead of apps/api (previews) */
  mock?: boolean;
  /** With `mock`: show the signed-out state */
  mockSignedOut?: boolean;
  /** With `mock`: which family to load */
  mockPersona?: MockPersona;
  /** Explicit overrides for the links and the See Jesus campaign; anything not given comes from MP (CUSTOMWIDGETS), then the defaults */
  settings?: Partial<WidgetSettings>;
  /** For the household map; without it the map slot says so and everything else still works */
  googleMapsApiKey?: string;
  /** Blur every personal fact (`.pii`) for a demo or a recording */
  blurPii?: boolean;
}

export function ProfileWidget(props: ProfileWidgetProps) {
  return (
    <ToastProvider>
      <ProfileWidgetInner {...props} />
    </ToastProvider>
  );
}

function ProfileWidgetInner({
  apiBaseUrl,
  mock = false,
  mockSignedOut = false,
  mockPersona,
  settings,
  googleMapsApiKey,
  blurPii = false,
}: ProfileWidgetProps) {
  const { status, ctx, locations, svgMap } = useProfileSession({
    apiBaseUrl,
    mock,
    mockSignedOut,
    mockPersona,
    settings,
  });

  // `@container` on the root: every column count below answers to the
  // widget's own width (the WordPress column), not the viewport.
  if (status === 'signedOut') {
    return (
      <CampusSvgProvider value={svgMap}>
        <div className="@container text-[#1c2b39]">
          <SignedOutCard mock={mock} />
        </div>
      </CampusSvgProvider>
    );
  }

  if (status === 'error') {
    return (
      <div
        className={`${CARD} @container flex flex-col items-center gap-3 px-6 py-14 text-center text-[#1c2b39]`}
      >
        <p className="text-lg font-bold">We couldn't load your profile right now.</p>
        <p className="max-w-md text-sm text-gray-600">Please try again in a moment.</p>
      </div>
    );
  }

  if (status === 'loading' || !ctx) {
    return (
      <div className="@container flex flex-col gap-12 text-[#1c2b39]" aria-busy="true">
        <HeroSkeleton />
        <Section kicker="Your details">
          <CardsSkeleton count={6} />
        </Section>
      </div>
    );
  }

  return (
    <CampusSvgProvider value={svgMap}>
      <ProfileContext.Provider value={ctx}>
        <div
          className={`@container flex flex-col gap-12 text-[#1c2b39] ${blurPii ? 'blur-pii' : ''}`}
        >
          <PeopleSection locations={locations} mapsApiKey={googleMapsApiKey} />
          <AccountSection />
        </div>
      </ProfileContext.Provider>
    </CampusSvgProvider>
  );
}
